import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useApi } from "@/lib/api";
import { PageTitle } from "@/components/Bits";
import QuestionDialog from "@/components/QuestionDialog";

export default function AdminQuestions() {
  const api = useApi();
  const [cats, setCats] = useState({});
  const [qs, setQs] = useState([]);
  const [cat, setCat] = useState("common");
  const [editing, setEditing] = useState(null);

  const load = () => api("get", "/admin/questions").then(setQs);
  useEffect(() => {
    api("get", "/meta").then((m) => setCats(m.categories));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const save = async (q) => {
    try {
      if (q.id) await api("put", `/admin/questions/${q.id}`, q);
      else await api("post", "/admin/questions", q);
      toast.success("Question saved");
      setEditing(null);
      load();
    } catch { toast.error("Could not save question"); }
  };
  const remove = async (q) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await api("delete", `/admin/questions/${q.id}`);
      toast.success("Question deleted");
      load();
    } catch { toast.error("Could not delete question"); }
  };
  const toggle = (q) => save({ ...q, active: !q.active });

  const list = qs.filter((q) => q.category === cat);
  return (
    <div className="fade-up" data-testid="admin-questions-page">
      <PageTitle kicker="Super admin" title="Question bank">
        <button onClick={() => setEditing({ category: cat, text: "", guidance: "", active: true })} data-testid="add-question-btn"
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 transition-colors"><Plus className="w-4 h-4" /> Add question</button>
      </PageTitle>
      <Tabs value={cat} onValueChange={setCat}>
        <TabsList className="bg-slate-900 border border-slate-800 rounded-none mb-6 flex-wrap h-auto">
          {Object.entries(cats).map(([k, v]) => (
            <TabsTrigger key={k} value={k} className="rounded-none" data-testid={`category-tab-${k}`}>{v} <span className="ml-2 font-mono text-[10px] text-slate-500">{qs.filter((q) => q.category === k).length}</span></TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="border border-slate-800 divide-y divide-slate-800">
        {list.map((q, i) => (
          <div key={q.id} className={`p-6 grid lg:grid-cols-12 gap-4 bg-slate-900 ${q.active ? "" : "opacity-50"}`} data-testid={`question-row-${q.id}`}>
            <div className="lg:col-span-5">
              <p className="font-mono text-[10px] text-slate-600 mb-1">{String(i + 1).padStart(2, "0")} · {q.id}</p>
              <p className="text-white font-medium">{q.text}</p>
            </div>
            <p className="lg:col-span-5 text-sm text-slate-400 leading-relaxed">{q.guidance}</p>
            <div className="lg:col-span-2 flex items-start justify-end gap-3">
              <Switch checked={q.active} onCheckedChange={() => toggle(q)} data-testid={`question-active-${q.id}`} />
              <button onClick={() => setEditing(q)} data-testid={`edit-question-${q.id}`} className="text-slate-400 hover:text-white transition-colors"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(q)} data-testid={`delete-question-${q.id}`} className="text-slate-400 hover:text-rose-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {!list.length && <p className="p-8 text-slate-500">No questions in this category.</p>}
      </div>
      <QuestionDialog value={editing} cats={cats} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}
